import type { PnlChartTagBarType } from "types/types";
import { PnlCategoriesEnum, ExtraPnlColumnsEnum } from "types/enums";
import { PRODUCING_CHART_TITLES } from "constants/constants";
import PnlChartLossCountGetter from "./data-getters/PnlChartLossCountGetter";
import { PnlColumnType } from "./pnlChartColumnsTypes";

/** defines the order that the Producing section bars appear in the Pnl chart */
const PRODUCING_CHART_ORDER = [
  PRODUCING_CHART_TITLES.effectiveProducingTime,
  PRODUCING_CHART_TITLES.qualityLoss,
  PRODUCING_CHART_TITLES.qualityLossWipIncrease,
  PRODUCING_CHART_TITLES.slowdownLoss,
  PRODUCING_CHART_TITLES.slowdownLossAndMinorStops,
  PRODUCING_CHART_TITLES.minorStopLoss,
  PRODUCING_CHART_TITLES.unknownMinorStopTime,
];

const MIN_BARS_TO_SHOW_BEFORE_BTN_SHOW_ALL = 4;

export function sortAndFilterPnlBars(bars: PnlChartTagBarType[]) {
  const sortedBars = bars
    .filter(bar => Math.abs(bar.time) > 0)
    .reduce((acc, bar) => {
      // sum any duplicates together (these come from trend chart reallocation feature)
      const barAlreadyInAcc = acc.find(
        barInAcc => barInAcc.title === bar.title
      );
      if (barAlreadyInAcc) {
        if (bar.count) {
          barAlreadyInAcc.count = (barAlreadyInAcc.count || 0) + bar.count;
        }
        barAlreadyInAcc.time += bar.time;
        if (bar.percent) {
          barAlreadyInAcc.percent =
            (barAlreadyInAcc.percent || 0) + bar.percent;
        }
        barAlreadyInAcc.percentWidth += bar.percentWidth;

        return acc;
      }
      return [...acc, bar];
    }, [] as PnlChartTagBarType[])
    .sort((bar1, bar2) =>
      bar1.category === PnlCategoriesEnum.producing &&
      bar2.category === PnlCategoriesEnum.producing
        ? PRODUCING_CHART_ORDER[bar2.title] - PRODUCING_CHART_ORDER[bar1.title]
        : bar2.time - bar1.time
    );

  const sortedBarsFirstFew = sortedBars.slice(
    0,
    MIN_BARS_TO_SHOW_BEFORE_BTN_SHOW_ALL
  );
  const sortedBarsFifthAndAfter = sortedBars.slice(
    MIN_BARS_TO_SHOW_BEFORE_BTN_SHOW_ALL
  );
  const sortedBarsAlwaysVisible = [
    ...sortedBarsFirstFew,
    ...sortedBarsFifthAndAfter.filter(
      bar => !bar.percent || Math.abs(Math.round(bar.percent * 100)) >= 1
    ),
  ];
  const sortedBarsInShowMore = sortedBarsFifthAndAfter.filter(
    bar => bar.percent && Math.abs(Math.round(bar.percent * 100)) < 1
  );
  return {
    sortedBars,
    sortedBarsAlwaysVisible,
    sortedBarsInShowMore,
  };
}

enum PnlColumnDataGetters {
  "PnlChartLossCountGetter",
}
/** each type of data may need a specific getter,
 * but multiple types of data may need the same getter,
 * so we can use this map to avoid fetching the same data multiple times
 */
const MAP_DATAGETTER_TO_DATAKEYS = {
  /** i.e. if one or more of these dataKeys is displayed, render the associated DataGetter */
  [PnlColumnDataGetters.PnlChartLossCountGetter]: [
    ExtraPnlColumnsEnum.outputLoss,
    ExtraPnlColumnsEnum.numberOfEvents,
    ExtraPnlColumnsEnum.averageDurationMs,
  ],
};
const MAP_DATAGETTERKEY_TO_DATAGETTER_COMPONENT = {
  [PnlColumnDataGetters.PnlChartLossCountGetter]: PnlChartLossCountGetter,
};

/** for each dataKey displayed, find & deduplicate the DataGetter components we need */
export function getDataGettersForColumns(pnlChartColumns: PnlColumnType[]) {
  const pnlColumnsDataKeys = pnlChartColumns.map(col => col.dataKey);

  return (
    Object.entries(MAP_DATAGETTER_TO_DATAKEYS)
      // filter down to the ones we need based on pnlColumnsDataKeys
      .filter(([dataGetterKey, associatedDataKeys]) =>
        associatedDataKeys.find(key => pnlColumnsDataKeys.includes(key))
      )
      // map each to its DataGetter component
      .map(
        ([dataGetterKey, _]) =>
          MAP_DATAGETTERKEY_TO_DATAGETTER_COMPONENT[dataGetterKey]
      )
  );
}

/** based on the available page width and each column's minWidthsPx,
 * generate pages where all columns stretch to fit comfortably into the pages
 */
export function constructPnlChartPages(
  pnlColumns: PnlColumnType[],
  breakpointKey: string,
  pageWidth: number,
  isPrint: boolean
) {
  return pnlColumns.reduce((accPages, column) => {
    // the first column gets added to the first page
    if (accPages.length === 0) {
      return [[column]];
    }

    // based on pageWidth and column minWidths, calculate pages
    // if the column fits into the current page,
    const columnWidth = column.minWidthsPx[breakpointKey];

    const currentPage = accPages[accPages.length - 1];
    const currentPageTotalWidthSoFar = currentPage.reduce(
      (accPageWidth, col) => accPageWidth + col.minWidthsPx[breakpointKey],
      0
    );

    const newPageWidthIfColumnIsInserted =
      currentPageTotalWidthSoFar + columnWidth;

    const doesColumnFitInPage = newPageWidthIfColumnIsInserted <= pageWidth;

    if (doesColumnFitInPage || isPrint) {
      // add it to the current page
      return [...accPages.slice(0, -1), [...currentPage, column]];
    } else {
      // else, add it to a new page
      return [...accPages, [column]];
    }
  }, [] as PnlColumnType[][]);
}
